/**
 * The claim-backed consent provider: read the fan's opt-in off their own ID token.
 *
 * **A sibling of `./provider.js`, at the seam that file describes**, and the reason that seam
 * exists. The machine, the store, the fingerprint, the gate and the five exported functions know
 * only `{ read, write }` and never learn where the boolean comes from.
 *
 * WHY THIS IS BETTER THAN A ROUND TRIP, AND IT IS NOT ONLY THAT IT IS FREE
 * -----------------------------------------------------------------------
 * `loyaltyoptin` is already a claim on the ID token this tenant issues — measured 2026-09-07 from a
 * real fan login, beside `cluboptin`, `3rdpartyoptin` and `finaloptin`. The whole
 * `App\Loyalty\Consent` read path exists to fetch that same value through the Auth0 Management API:
 * a machine-to-machine credential, a token mint, a per-fan server cache, two throttle buckets and a
 * breaker. Reading the claim is zero requests **and stronger evidence**, because the Auth0 SDK
 * verifies the token's signature before any claim is exposed — where the endpoint's answer is a
 * `POST` whose freshness this side has to take on trust.
 *
 * Both repositories believed this was blocked. `outlier-stats`' own consent doc names a
 * `createTokenClaimConsentProvider` reading a namespaced claim as the planned end state and records
 * it as waiting on *"the tenant owners adding an Auth0 Action"*. There was nothing to wait for.
 *
 * WHY IT IS OFF BY DEFAULT, WHICH IS THE PART TO READ BEFORE SWITCHING IT ON
 * -------------------------------------------------------------------------
 * Two things it does not fix, and neither is a bug in this file:
 *
 * 1. **A claim is exactly as old as the token carrying it.** The endpoint read is current; this one
 *    is stale until the fan gets a new token. `server_ttl` in `config/three-halves.php` is a bound
 *    this repository *chose*, with a comment explaining that it is a compliance number rather than
 *    a cache tunable — an ID-token lifetime is not a number anybody here chose, and the
 *    withdrawal-made-in-My-Account question applies to this path with a bound nobody here controls.
 * 2. **A fan who consents mid-session holds a token minted before they did.** This answers `false`
 *    for them until they get a new one. `write()` below papers over the commonest shape of that —
 *    the fan consenting on this very page — and does not fix the general case.
 *
 * So `config/three-halves.php`'s `read_from` defaults to `'endpoint'`, and switching it is a
 * compliance decision. See docs/subsystems/loyalty-consent.md.
 *
 * **The WRITE is not claim-shaped and never can be**, so it is delegated. A claim is something
 * Auth0 puts on a token; the only way to change it is to change the `app_metadata` behind it, which
 * is the Management API. That asymmetry is why this takes a writer rather than implementing one.
 */

import { LoyaltyConsentAuthError, LoyaltyConsentError } from './errors.js';

/**
 * Build a provider that reads the claim and delegates the write.
 *
 * @param {Object}                 options           Inputs.
 * @param {() => (Object|null)}    options.getClaims The fan's ID-token claims, or null.
 * @param {string}                 options.claimKey  Which claim carries the opt-in.
 * @param {() => number}           options.now       Epoch milliseconds.
 * @param {{write: Function}|null} options.writer    Where a write goes. Null when unavailable.
 * @return {{read: Function, write: Function}} The provider.
 */
export function createClaimConsentProvider( { getClaims, claimKey, now, writer } ) {
	/**
	 * Interpret the claim.
	 *
	 * **Only a real boolean `true` grants**, and everything else is a refusal rather than a coercion.
	 * The same rule `machine.js` applies to the endpoint's payload, for the same reason: a consent
	 * record is the one place a truthy string must not become a yes. Auth0 stores whatever
	 * `app_metadata` was written, so a `"true"`, a `1` or an absent key are all reachable.
	 *
	 * @param {Object} claims The ID-token claims.
	 * @return {boolean} Whether consent is granted.
	 */
	function granted( claims ) {
		return claims[ claimKey ] === true;
	}

	return {
		/**
		 * The fan's consent, off their own token.
		 *
		 * Rejects with `LoyaltyConsentAuthError` when there is nobody to read for — the same
		 * before-the-wire refusal `./provider.js` makes, and what `machine.js` turns into the `anon`
		 * state rather than a denial.
		 *
		 * **An absent claim is a definite `false`, not an unreadable answer.** That is the one
		 * judgement in this file. The claim set is complete by construction — the SDK exposes the
		 * whole verified payload — so a missing `loyaltyoptin` means the fan has none, exactly as the
		 * Management API answers for a fan with no such `app_metadata` key. Treating it as
		 * unreadable would publish `unavailable` for every fan who has simply never opted in.
		 *
		 * @return {Promise<{granted: boolean, checkedAt: number}>} The confirmed value.
		 */
		read() {
			const claims = getClaims();

			if ( ! claims || typeof claims !== 'object' ) {
				return Promise.reject( new LoyaltyConsentAuthError() );
			}

			return Promise.resolve( { granted: granted( claims ), checkedAt: now() } );
		},

		/**
		 * Persist the fan's consent, through whoever can actually write it.
		 *
		 * **The answer is the WRITER's, not the claim's**, and that is deliberate rather than
		 * lazy. `machine.js` caches only what a write confirmed, so answering from the token here
		 * would cache the value the token had *before* the write — which is the mid-session case
		 * this whole provider is weakest on, made worse. Taking the writer's answer means a fan who
		 * consents on this page is granted on this page, whatever their token still says.
		 *
		 * @param {boolean} value True to grant, false to revoke.
		 * @return {Promise<{granted: boolean, checkedAt: number}>} What the writer confirmed.
		 */
		write( value ) {
			if ( ! writer || typeof writer.write !== 'function' ) {
				return Promise.reject(
					new LoyaltyConsentError( 'Loyalty consent cannot be written on this install.' )
				);
			}

			return writer.write( value );
		},
	};
}
